import { manageAPI } from "./index";


/**
 * Notify backend after upload
 * @param {Object} params
 * @param {string} params.key
 * @param {string} params.originalFileName
 * @param {number} params.numRows
 * @param {Object} params.mapping
 * @returns {Promise<any>}
 */
export async function notifyCsvUpload({ key, originalFileName, numRows, mapping }) {
  return (await manageAPI.post("crm/people/csv", { key, original_file_name: originalFileName, num_rows: numRows, mapping })).data;
}

/**
 * Fetch CRM upload jobs history
 * @returns {Promise<any>} Response with jobs array
 */
export async function getCrmUploadJobs() {
  return (await manageAPI.get("crm/people/csv")).data;
}

/**
 * Fetch CRM broadcasts
 * @returns {Promise<any>} Response with broadcasts array
 */
export async function getCrmBroadcasts() {
  return (await manageAPI.get("crm/broadcasts")).data;
}

/**
 * Fetch a single CRM broadcast by ID
 * @param {string} id - Broadcast ID
 * @returns {Promise<any>} Response with broadcast data
 */
export async function getCrmBroadcast(id) {
  return (await manageAPI.get(`crm/broadcasts/${id}`)).data;
}

/**
 * Create a new CRM broadcast
 * @param {Object} payload
 * @param {string} payload.name
 * @param {string} payload.description
 * @param {string} payload.subject
 * @param {string} payload.schedule
 * @param {string} payload.html
 * @returns {Promise<any>} Response with created broadcast
 */
export async function createCrmBroadcast(payload) {
  return (await manageAPI.post("crm/broadcasts", payload)).data;
}

/**
 * Update an existing CRM broadcast
 * @param {string} id - Broadcast ID
 * @param {Object} payload
 * @param {string} payload.name
 * @param {string} payload.description
 * @param {string} payload.subject
 * @param {string} payload.schedule
 * @param {string} payload.html
 * @returns {Promise<any>} Response with updated broadcast
 */
export async function updateCrmBroadcast(id, payload) {
  return (await manageAPI.put(`crm/broadcasts/${id}`, payload)).data;
}

/**
 * Get CRM settings
 * @returns {Promise<any>} Response with CRM settings
 */
export async function getCrmSettings() {
  return (await manageAPI.get("crm/settings")).data;
}

/**
 * Update CRM settings
 * @param {Object} payload
 * @param {Array<string>} payload.domains
 * @param {string} payload.default_from_address
 * @param {string} payload.default_reply_to_address
 * @param {string} payload.default_header_html
 * @param {Object} payload.default_header_design
 * @param {boolean} payload.development_mode
 * @returns {Promise<any>} Response with updated settings
 */
export async function updateCrmSettings(payload) {
  return (await manageAPI.put("crm/settings", payload)).data;
}

/**
 * Send a test email for a CRM broadcast
 * @param {string} emailId - The ID of the email/broadcast
 * @param {string} email - Email address to send test to
 * @returns {Promise<any>} Response with test email result
 */
export async function sendBroadcastTestEmail(emailId, email) {
  return (await manageAPI.post(`crm/emails/${emailId}/send/example`, { email })).data;
}

/**
 * Fetch CRM campaigns
 * @returns {Promise<any>} Response with campaigns array
 */
export async function getCrmCampaigns() {
  return (await manageAPI.get("crm/campaigns")).data;
}

/**
 * Fetch a single CRM campaign by ID
 * @param {string} id - Campaign ID
 * @returns {Promise<any>} Response with campaign data
 */
export async function getCrmCampaign(id) {
  return (await manageAPI.get(`crm/campaigns/${id}`)).data;
}

/**
 * Create a new CRM campaign
 * @param {Object} payload
 * @param {string} payload.name
 * @param {string} payload.description
 * @param {string} payload.type
 * @param {boolean} payload.active
 * @param {string} payload.trigger_event
 * @param {number} payload.delay_value
 * @param {string} payload.delay_unit
 * @param {Object} payload.email
 * @returns {Promise<any>} Response with created campaign
 */
export async function createCrmCampaign(payload) {
  return (await manageAPI.post("crm/campaigns", payload)).data;
}

/**
 * Update an existing CRM campaign
 * @param {string} id - Campaign ID
 * @param {Object} payload
 * @param {string} payload.name
 * @param {string} payload.description
 * @param {string} payload.type
 * @param {boolean} payload.active
 * @param {string} payload.trigger_event
 * @param {number} payload.delay_value
 * @param {string} payload.delay_unit
 * @param {Object} payload.email
 * @returns {Promise<any>} Response with updated campaign
 */
export async function updateCrmCampaign(id, payload) {
  return (await manageAPI.put(`crm/campaigns/${id}`, payload)).data;
}

/**
 * Delete a CRM campaign
 * @param {string} id - Campaign ID
 * @returns {Promise<any>} Response with deletion result
 */
export async function deleteCrmCampaign(id) {
  return (await manageAPI.delete(`crm/campaigns/${id}`)).data;
}

/**
 * Toggle campaign active status
 * @param {string} id - Campaign ID
 * @param {boolean} active - New active status
 * @returns {Promise<any>} Response with updated campaign
 */
export async function toggleCrmCampaignStatus(id, active) {
  return (await manageAPI.patch(`crm/campaigns/${id}/status`, { active })).data;
}

/**
 * Get campaign templates by type
 * @param {string} type - Campaign type (welcome, cart_abandonment, etc.)
 * @returns {Promise<any>} Response with campaign templates
 */
export async function getCrmCampaignTemplates(type) {
  return (await manageAPI.get(`crm/campaign/${type}/templates`)).data;
}

/**
 * Generate email template with AI
 * @param {Object} payload
 * @param {string} payload.prompt - AI generation prompt
 * @param {Object} payload.design - Current email design (optional)
 * @param {string} payload.campaign_type - Campaign type (welcome, cart_abandonment, etc.)
 * @returns {Promise<any>} Response with generated template
 */
export async function generateTemplateWithAI(payload) {
  return (await manageAPI.post("crm/template/generate", payload, {
    timeout: 90000, // 90 seconds timeout for AI generation
  })).data;
}

/**
 * Duplicate a CRM broadcast
 * @param {string} id - Broadcast ID to duplicate
 * @param {string} name - Name for the duplicated broadcast
 * @returns {Promise<any>} Response with duplicated broadcast
 */
export async function duplicateCrmBroadcast(id, name) {
  return (await manageAPI.post(`crm/broadcasts/${id}/duplicate`, { name })).data;
}
